import { readProblem } from '../../components/data-grid/problem';
import type { Problem } from '../../components/data-grid/problem';
import type { DocumentPeriod, DocumentRow, DocumentStatus } from './filters';

/** `authorizedFetch` from the auth provider; the token never reaches a screen. */
export type FetchLike = (path: string, init?: RequestInit) => Promise<Response>;

export class ApiError extends Error {
  constructor(readonly problem: Problem) {
    super(problem.detail);
    this.name = 'ApiError';
  }
}

export async function requestJson<T>(fetcher: FetchLike, path: string, init?: RequestInit): Promise<T> {
  const response = await fetcher(path, {
    ...init,
    headers: init?.body === undefined ? init?.headers : { 'content-type': 'application/json', ...init?.headers },
  });
  if (!response.ok) {
    throw new ApiError(await readProblem(response));
  }
  return (await response.json()) as T;
}

export interface LocalizedText {
  az: string;
  en: string;
  ru: string;
}

export interface DocumentTypeDto {
  code: string;
  nameI18n: LocalizedText;
}

export interface DocumentLineDto {
  id: string;
  lineNo: number;
  data: Record<string, unknown>;
  amount: string;
  dimensions: Record<string, string>;
}

export interface DocumentDto {
  id: string;
  type: string;
  operationKind: string;
  orgId: string;
  number: string;
  date: string;
  status: DocumentStatus;
  author: string;
  data: Record<string, unknown>;
  lines: DocumentLineDto[];
  basedOnDocId?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface PeriodDto {
  id: string;
  orgId: string;
  year: number;
  month: number;
  status: 'open' | 'closing' | 'closed';
}

function query(params: Record<string, string | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== '') {
      search.set(key, value);
    }
  }
  const text = search.toString();
  return text === '' ? '' : `?${text}`;
}

export async function loadDocumentTypes(fetcher: FetchLike): Promise<DocumentTypeDto[]> {
  return requestJson<DocumentTypeDto[]>(fetcher, '/document-types');
}

export async function loadPeriods(fetcher: FetchLike): Promise<DocumentPeriod[]> {
  const periods = await requestJson<PeriodDto[]>(fetcher, '/periods');
  return periods.map((period) => ({
    id: period.id,
    year: period.year,
    month: period.month,
    status: period.status,
    orgId: period.orgId,
  }));
}

export async function loadDocuments(
  fetcher: FetchLike,
  filter: { type: string; status: string; orgId?: string },
): Promise<DocumentDto[]> {
  return requestJson<DocumentDto[]>(
    fetcher,
    `/documents${query({
      type: filter.type === '' ? undefined : filter.type,
      status: filter.status === 'all' ? undefined : filter.status,
      orgId: filter.orgId === undefined || filter.orgId === '' ? undefined : filter.orgId,
    })}`,
  );
}

function describe(data: Record<string, unknown>): string {
  const candidates = ['description', 'memo', 'reason', 'purpose', 'note'];
  for (const key of candidates) {
    const value = data[key];
    if (typeof value === 'string' && value !== '') {
      return value;
    }
  }
  return '';
}

export function toDocumentRow(
  document: DocumentDto,
  typeName: string,
  total: string,
): DocumentRow {
  return {
    id: document.id,
    number: document.number,
    type: document.type,
    typeName,
    date: document.date,
    status: document.status,
    author: document.author,
    description: describe(document.data),
    amount: total,
    lines: document.lines.map((line) => ({ amount: line.amount })),
  };
}

export function localizedName(text: LocalizedText | undefined, language: 'az' | 'en' | 'ru'): string {
  if (text === undefined) {
    return '';
  }
  return text[language] ?? text.az ?? text.en ?? '';
}
